import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Container, Grid, SxProps, Typography } from '@mui/material';

import { isFirstLoginKey } from '@/config/axios';
import { useTranslation } from '@/hooks/translations';
import { Logo } from '@/components/icon/logo';
import { ILoginResponse } from '@/interfaces/auth/reponse';

import LanguageSelector from '@/components/NavBar/LanguageSelector';
import AuthStorage from '@/services/auth/storage';
import { TokenTypeEnum } from '@/services/auth/storage/types';
import LoginComponent from './login';
import SelectRestaurant from './select-restaurant';
import Enable from './enable';
import Mfa from './mfa';
import DeviceLogin from './device';

type PageType = 'login' | 'device-login' | 'mfa-code' | 'mfa-enable';

export const disablePasswordLogin = Boolean(process.env.NEXT_PUBLIC_DISABLE_PASSWORD_LOGIN || false);

export type ILoginProps = {
    loading: boolean;
    loginAsAdmin?: boolean;
    setLoading: (loading: boolean) => void;
    setError: (error: string) => void;
    setPage?: (page: PageType) => void;
    accessSelectRestaurant: (
        res: ILoginResponse,
        options?: {
            forceAdmin?: boolean;
            userProvider?: string;
            withOtp?: boolean;
        },
    ) => void;
};

const TypographyProps: SxProps = {
    textAlign: 'center',
    backgroundColor: 'primary.light',
    padding: '0.8rem',
    marginBottom: '1rem',
    borderRadius: '0.3rem',
    fontWeight: 700,
};

const Login = () => {
    const { t } = useTranslation('common');

    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const [loginAsAdmin, setLoginAsAdmin] = useState<boolean>(false);
    const [loginResponse, setLoginResponse] = useState<ILoginResponse>();

    const [selectRestaurant, setSelectRestaurant] = useState<boolean>(false);
    const [page, setPage] = useState<PageType>('login');

    useEffect(() => {
        if (!AuthStorage.getAccessToken() || !AuthStorage.getRefreshToken()) {
            return;
        }

        setSelectRestaurant(true);
    }, []);

    useEffect(() => {
        setError('');
    }, [page]);

    const accessSelectRestaurant = (
        { data }: ILoginResponse,
        options?: {
            forceAdmin?: boolean;
            userProvider?: string;
            withOtp?: boolean;
        },
    ) => {
        AuthStorage.setCognitoToken(data.access_token || '');
        AuthStorage.setAccessToken(data.token || '');
        AuthStorage.setRefreshToken(data.refreshToken || '');
        if (options?.withOtp) {
            AuthStorage.setTokenType(TokenTypeEnum.Device);
        } else if (options?.forceAdmin || loginAsAdmin) {
            AuthStorage.setTokenType(TokenTypeEnum.Admin);
        } else {
            AuthStorage.setTokenType(TokenTypeEnum.Vendor, options?.userProvider);
        }
        AuthStorage.setUserId(data.userId || data.id);
        AuthStorage.setUsername(data.name || '');
        AuthStorage.setUserRole(data.role || '');

        localStorage.setItem(isFirstLoginKey, data.email || '');

        setSelectRestaurant(true);
    };

    const getTitle = () => {
        if (selectRestaurant) {
            return t('Select Your Restaurant');
        }
        switch (page) {
            default:
            case 'login':
                return t('Vendor Login');
            case 'device-login':
                return t('OTP Login');
            case 'mfa-enable':
                return t('Enable Multi-factor Authentication');
            case 'mfa-code':
                return t('Multi-factor Authentication');
        }
    };

    const getContent = () => {
        if (selectRestaurant) {
            return <SelectRestaurant setSelectRestaurant={setSelectRestaurant} />;
        }

        switch (page) {
            default:
            case 'login':
                return (
                    <LoginComponent
                        loginAsAdmin={loginAsAdmin}
                        setLoginAsAdmin={setLoginAsAdmin}
                        accessSelectRestaurant={accessSelectRestaurant}
                        setLoginResponse={setLoginResponse}
                        setPage={setPage}
                        setError={setError}
                        loading={loading}
                        setLoading={setLoading}
                    />
                );
            case 'device-login':
                return (
                    <DeviceLogin
                        accessSelectRestaurant={accessSelectRestaurant}
                        setLoginResponse={setLoginResponse}
                        setPage={setPage}
                        setError={setError}
                        loading={loading}
                        setLoading={setLoading}
                    />
                );
            case 'mfa-enable':
                return (
                    <Enable
                        loading={loading}
                        setLoading={setLoading}
                        setError={setError}
                        loginResponse={loginResponse}
                        loginAsAdmin={loginAsAdmin}
                        accessSelectRestaurant={accessSelectRestaurant}
                        setPage={setPage}
                    />
                );
            case 'mfa-code':
                return (
                    <Mfa
                        loading={loading}
                        accessSelectRestaurant={accessSelectRestaurant}
                        loginResponseData={loginResponse?.data}
                        setError={setError}
                        setLoading={setLoading}
                    />
                );
        }
    };
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                <LanguageSelector />
            </Box>

            <Container maxWidth="xs">
                <Grid container>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Logo height="50" style={{ margin: '2.2rem' }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography color="primary" sx={TypographyProps} variant="h6">
                                    {getTitle()}
                                </Typography>
                                {getContent()}
                                {error && (
                                    <Typography sx={{ textAlign: 'center' }} color="error">
                                        {error}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Login;
